import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

/* ─────────────────────────────────────────────────────────────
   Reveal — scroll-into-view fade + lift.

   Uses a self-managed IntersectionObserver (instead of framer's
   whileInView) plus a safety fallback so content is NEVER left
   permanently hidden — on mobile / hydrated prerenders the
   whileInView trigger could occasionally miss, hiding sections.
───────────────────────────────────────────────────────────── */
export default function Reveal({
  children,
  delay = 0,
  y = 22,
  className = "",
}) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (shown) return;
    const el = ref.current;

    // No element or no IO support → just show the content.
    if (!el || typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }

    const reveal = () => setShown(true);

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          reveal();
          io.disconnect();
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.01 }
    );
    io.observe(el);

    // Safety net: if the observer never fires (fast scroll, hydration
    // race, odd mobile behavior), reveal anyway so nothing stays hidden.
    const fallback = window.setTimeout(reveal, 1500);

    return () => {
      io.disconnect();
      window.clearTimeout(fallback);
    };
  }, [shown]);

  return (
    <motion.div
      ref={ref}
      initial={false}
      animate={{ opacity: shown ? 1 : 0, y: shown ? 0 : y }}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
      style={{ willChange: "opacity, transform" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}
